/**
 * ContentOpportunities Component
 *
 * Displays proactive content improvement opportunities for content managers.
 * Shows which critical specs are missing values across product categories.
 *
 * Part of Story 68: Content Improvement Opportunities Dashboard
 * Date: January 11, 2026
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import {
  Loader2,
  Sparkles,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Target
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

interface MissingSpec {
  spec: string;
  displayName: string;
  severity: string;
  description: string;
  missingCount: number;
  hasValueCount: number;
  missingRate: number;
  sampleMissingProducts: Array<{
    id: string;
    productId: string;
    name: string;
  }>;
}

interface EnrichmentOpportunity {
  type: 'missing_critical_specs';
  category: string;
  categoryShort: string;
  totalProducts: number;
  specs: MissingSpec[];
  totalMissingCount: number;
  severity: string;
  action: {
    type: string;
    label: string;
    description: string;
  };
}

interface OpportunitiesData {
  enrichment: {
    opportunities: EnrichmentOpportunity[];
    total: number;
    categories: number;
  };
  summary: {
    totalOpportunities: number;
    enrichmentCount: number;
  };
}

interface ContentOpportunitiesProps {
  siteId: string;
  compact?: boolean;
  onResearchClick?: (category: string, attribute?: string) => void;
}

const severityColors: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-blue-100 text-blue-800 border-blue-200'
};

const severityBadgeVariants: Record<string, 'destructive' | 'secondary' | 'outline' | 'default'> = {
  critical: 'destructive',
  high: 'destructive',
  medium: 'secondary',
  low: 'outline',
  CRITICAL: 'destructive',
  HIGH: 'destructive',
  MEDIUM: 'secondary',
  LOW: 'outline'
};

export function ContentOpportunities({ siteId, compact = false, onResearchClick }: ContentOpportunitiesProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<OpportunitiesData | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadOpportunities();
  }, [siteId]);

  const loadOpportunities = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(`/api/content-opportunities/${siteId}`);
      if (!response.ok) throw new Error('Failed to load opportunities');
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const handleResearch = (category: string, attribute?: string) => {
    if (onResearchClick) {
      // Navigate to Research Product tab with filter
      onResearchClick(category, attribute);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Analyzing product specs...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const opportunities = data?.enrichment?.opportunities || [];

  if (!data || opportunities.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
          <p>No critical spec gaps detected</p>
          <p className="text-sm">All product categories have good spec coverage</p>
        </CardContent>
      </Card>
    );
  }

  // Compact view for embedding
  if (compact) {
    const totalMissing = opportunities.reduce((sum, o) => sum + o.totalMissingCount, 0);
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Target className="h-4 w-4 text-amber-600" />
            {opportunities.length} Categories Need Spec Research
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="text-sm text-amber-800">
            {totalMissing} products missing critical specifications
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Target className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold">Missing Critical Specs</h3>
            <p className="text-sm text-muted-foreground">
              {opportunities.length} categories have products missing important specifications
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadOpportunities}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Opportunities List */}
      <div className="space-y-3">
        {opportunities.map((opp) => (
          <Card
            key={opp.category}
            className={`border-l-4 ${
              opp.severity === 'critical' ? 'border-l-red-500' :
              opp.severity === 'high' ? 'border-l-orange-500' :
              opp.severity === 'medium' ? 'border-l-yellow-500' : 'border-l-blue-500'
            }`}
          >
            <CardContent className="py-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Category Header */}
                  <div className="flex items-center gap-2 mb-2">
                    <button
                      onClick={() => toggleCategory(opp.category)}
                      className="flex items-center gap-1 font-medium hover:text-primary"
                    >
                      {expandedCategories.has(opp.category) ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                      {opp.categoryShort}
                    </button>
                    <Badge variant={severityBadgeVariants[opp.severity]}>
                      {opp.severity}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {opp.totalProducts} products
                    </span>
                  </div>

                  {/* Specs Summary */}
                  <div className="flex flex-wrap gap-2 mb-2">
                    {opp.specs.slice(0, 3).map((spec) => (
                      <button
                        key={spec.spec}
                        onClick={() => handleResearch(opp.category, spec.spec)}
                        disabled={!onResearchClick}
                        className="flex items-center gap-1 text-xs bg-muted px-2 py-1 rounded hover:bg-primary/10 transition-colors cursor-pointer disabled:cursor-default disabled:hover:bg-muted"
                        title={`Research products missing ${spec.displayName}`}
                      >
                        <span className="font-medium">{spec.displayName}</span>
                        <span className="text-muted-foreground">
                          {spec.missingCount} missing ({spec.missingRate}%)
                        </span>
                      </button>
                    ))}
                    {opp.specs.length > 3 && (
                      <span className="text-xs text-muted-foreground">
                        +{opp.specs.length - 3} more
                      </span>
                    )}
                  </div>

                  {/* Expanded Details */}
                  {expandedCategories.has(opp.category) && (
                    <div className="mt-4 pt-4 border-t space-y-3">
                      <div className="text-xs text-muted-foreground mb-2">
                        Full category: {opp.category}
                      </div>
                      {opp.specs.map((spec) => (
                        <div key={spec.spec} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{spec.displayName}</span>
                              <Badge variant={severityBadgeVariants[spec.severity]} className="text-xs">
                                {spec.severity}
                              </Badge>
                            </div>
                            <span className="text-sm">
                              {spec.missingCount} / {spec.missingCount + spec.hasValueCount} missing
                            </span>
                          </div>
                          <Progress value={100 - spec.missingRate} className="h-2" />
                          <p className="text-xs text-muted-foreground">{spec.description}</p>
                          {spec.sampleMissingProducts.length > 0 && (
                            <div className="text-xs text-muted-foreground">
                              Examples: {spec.sampleMissingProducts.slice(0, 3).map(p => p.name).join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Action Button */}
                <Button
                  size="sm"
                  onClick={() => handleResearch(opp.category, opp.specs[0]?.spec)}
                  disabled={!onResearchClick}
                  className="ml-4"
                  title="Open Research Product tab filtered by this category"
                >
                  <Sparkles className="h-4 w-4 mr-1" />
                  Research
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default ContentOpportunities;
